import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScoreComparisonBar } from '~/components/common/score-comparison-bar';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { formatCompactNumber } from '~/utils/formatting';
import {
  type AttributeCategorySize,
  buildAttributeCategoryView,
  hasCategories,
  humanizeCategory,
} from '~/utils/popular-attributes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AttributeItem {
  /** Attribute label. Pre-translated by the caller. */
  title: string;
  /** Before-enrichment score, relative to `maxScore`. */
  beforeScore: number;
  /** After-enrichment score, relative to `maxScore`. */
  afterScore: number;
  /** Upper bound for beforeScore/afterScore (e.g. 10 or 100). Defaults to 10. */
  maxScore?: number;
  /** Products with the attribute populated before enrichment. Null when the
   *  source never computed it — the row then falls back to the score readout. */
  beforeCount?: number | null;
  /** Products with the attribute populated after enrichment. Null when uncomputed. */
  afterCount?: number | null;
  /** Monthly search volume for this attribute. Null when Google has none —
   *  the subtitle line is then omitted rather than showing "0 searches/mo". */
  monthlySearches?: number | null;
  /** Raw product-category taxonomy slug (e.g. `"apparel-and-accessories"`),
   *  or `null` for the backend's catalog-wide row. When at least one item in
   *  the list carries a non-null value here, the list gains a category
   *  selector: a specific category filters down to just that category's
   *  rows, and "All" merges every label to one row by summing its category
   *  rows (see `~/utils/popular-attributes`) — omit entirely for callers with
   *  no category concept. */
  productCategory?: string | null;
  /** Shared cohort size behind this specific row. Independent of the
   *  list-wide `sampleSize` prop. */
  sampleSize?: number | null;
}

interface AttributeScoreListProps {
  /** Section heading. Pre-translated by the caller. */
  title: string;
  /** Section sub-heading. Pre-translated by the caller. Ignored once the list
   *  gains a category selector — that state builds its own dynamic subtitle
   *  instead (see `AttributeItem.productCategory`). */
  subtitle: string;
  /** Ordered list of attributes to display. */
  attributes: AttributeItem[];
  /** When true, hides the Before/After color legend above the rows. */
  hideLegend?: boolean;
  /** When true, passes hideLabels to ScoreComparisonBar, suppressing per-row Before/After text. */
  hideComparisonLabel?: boolean;
  /** Shared cohort size behind the per-row counts, shown as a header caption.
   *  Omit when it can't be derived — the caption then disappears rather than
   *  asserting a denominator the data doesn't support. Ignored once the list
   *  gains a category selector — the dynamic subtitle carries this instead. */
  sampleSize?: number;
  /** Every product category of the card with its product count
   *  (`POPULAR_ATTRIBUTE_CATEGORIES`). When present, the dropdown counts,
   *  the subtitle and every bar are measured against these sizes, and "All"
   *  is the sum of the categories. Omit for payloads that predate it. */
  categorySizes?: AttributeCategorySize[];
  /** `data-testid` root; derives `-title`, `-category-select`, `-legend-before`, `-row-{attribute}`, `-empty`. */
  testId?: string;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Clickable legend pill: a colored circle next to a label. Clicking solos its
 * series (shows only that row); an inactive item dims to signal it's filtered.
 */
function LegendItem({
  color,
  label,
  active,
  onClick,
  testId,
}: {
  color: 'before' | 'after';
  label: string;
  testId: string;
  /** Whether this series is currently shown. */
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      data-testid={testId}
      onClick={onClick}
      className={`flex cursor-pointer items-center gap-1.5 transition-opacity ${active ? 'opacity-100' : 'opacity-40'}`}
    >
      <span
        aria-hidden="true"
        className={`h-3 w-3 shrink-0 rounded-full ${color === 'before' ? 'bg-warning' : 'bg-primary'}`}
      />
      <span className="text-text-date-pill text-sm">{label}</span>
    </button>
  );
}

function CategoryLabel({ slug, className = '' }: { slug: string; className?: string }) {
  return <span className={className}>{humanizeCategory(slug)}</span>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a titled attribute list where each row shows an attribute name and
 * a before/after score bar. A color legend at the top replaces the per-row
 * Before/After labels (hideLabels is passed to ScoreComparisonBar).
 *
 * Gains a category selector (defaulting to "All", every label merged to one
 * row) whenever the data carries a `productCategory` on at least one item —
 * purely data-driven, so callers with no category concept (Optimization
 * Quality's two lists, the trial steps) render exactly as before.
 */
export function AttributeScoreList({
  title,
  subtitle,
  attributes,
  hideLegend = false,
  hideComparisonLabel = false,
  sampleSize,
  categorySizes,
  testId = 'attribute-score-list',
}: AttributeScoreListProps) {
  const { t } = useTranslation('common');
  // Legend filter: null shows both series (default); 'before'/'after' solos one.
  const [soloSeries, setSoloSeries] = useState<'before' | 'after' | null>(null);
  const beforeVisible = soloSeries !== 'after';
  const afterVisible = soloSeries !== 'before';

  const hasCategoryFilter = hasCategories(attributes);
  // null = "All" — one merged row per attribute.
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // One computation feeds the rows, the dropdown and the subtitle, so they
  // can't disagree. Zero-coverage attributes are hidden per view.
  const view = hasCategoryFilter
    ? buildAttributeCategoryView(attributes, categorySizes, selectedCategory)
    : null;
  const visibleAttributes = view ? view.rows : attributes;
  const selectedCount = view?.selectedCount ?? 0;

  return (
    <div data-testid={testId} className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex flex-1 flex-row items-center justify-between">
            <div className="flex items-baseline gap-2">
              <h3 data-testid={`${testId}-title`} className="text-foreground text-md font-medium">
                {title}
              </h3>

              {/* Cohort caption — one shared denominator for every row below.
                  Superseded by the dynamic subtitle once a category selector
                  is present, so it doesn't say the same thing twice. */}
              {!hasCategoryFilter && sampleSize !== undefined && (
                <span
                  data-testid={`${testId}-sample-size`}
                  className="text-muted-foreground text-xs font-light"
                >
                  {t('attributeScoreList.sampleSize', {
                    count: sampleSize,
                    formatted: sampleSize.toLocaleString(),
                  })}
                </span>
              )}
            </div>

            <div className="flex items-center gap-4">
              {hasCategoryFilter && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="cursor-pointer gap-1.5"
                      data-testid={`${testId}-category-select`}
                    >
                      {selectedCategory ? (
                        <CategoryLabel slug={selectedCategory} />
                      ) : (
                        t('attributeScoreList.categoryAll')
                      )}
                      <ChevronDown className="size-3.5" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" data-testid={`${testId}-category-menu`}>
                    <DropdownMenuCheckboxItem
                      checked={selectedCategory === null}
                      data-testid={`${testId}-category-option-all`}
                      onCheckedChange={() => {
                        setSelectedCategory(null);
                      }}
                      className="cursor-pointer"
                    >
                      {t('attributeScoreList.categoryAll')}
                      {' — '}
                      {t('attributeScoreList.categoryProductCount', {
                        count: view?.allCount ?? 0,
                        formatted: (view?.allCount ?? 0).toLocaleString(),
                      })}
                    </DropdownMenuCheckboxItem>
                    {(view?.options ?? []).map((option) => (
                      <DropdownMenuCheckboxItem
                        key={option.slug}
                        checked={selectedCategory === option.slug}
                        data-testid={`${testId}-category-option-${option.slug}`}
                        onCheckedChange={() => {
                          setSelectedCategory(option.slug);
                        }}
                        className="cursor-pointer"
                      >
                        <CategoryLabel slug={option.slug} />
                        {' — '}
                        {t('attributeScoreList.categoryProductCount', {
                          count: option.count,
                          formatted: option.count.toLocaleString(),
                        })}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Legend — hidden when no data (nothing to compare) or caller
                  opts out. Clicking an item solos that series; both show by
                  default. */}
              {!hideLegend && visibleAttributes.length > 0 && (
                <div className="flex items-center gap-4">
                  <LegendItem
                    color="before"
                    label={t('scoreComparisonBar.beforeLabel')}
                    active={beforeVisible}
                    testId={`${testId}-legend-before`}
                    onClick={() => {
                      setSoloSeries((prev) => (prev === 'before' ? null : 'before'));
                    }}
                  />
                  <LegendItem
                    color="after"
                    label={t('scoreComparisonBar.afterLabel')}
                    active={afterVisible}
                    testId={`${testId}-legend-after`}
                    onClick={() => {
                      setSoloSeries((prev) => (prev === 'after' ? null : 'after'));
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <p
            data-testid={`${testId}-subtitle`}
            className="text-muted-foreground text-sm font-light"
          >
            {hasCategoryFilter ? (
              <>
                {t('attributeScoreList.categorySubtitlePrefix', {
                  count: selectedCount,
                  formatted: selectedCount.toLocaleString(),
                })}
                {selectedCategory && (
                  <>
                    {' '}
                    {t('attributeScoreList.categorySubtitleIn')}{' '}
                    <CategoryLabel
                      slug={selectedCategory}
                      className="text-foreground font-medium"
                    />
                  </>
                )}
                .
              </>
            ) : (
              subtitle
            )}
          </p>
        </div>
      </div>

      {/* Row list */}
      <div
        data-testid={`${testId}-list`}
        className="bg-surface-blue max-h-110 overflow-hidden overflow-y-scroll rounded-sm p-3"
      >
        {visibleAttributes.length === 0 && (
          <p
            data-testid={`${testId}-empty`}
            className="text-muted-foreground py-8 text-center text-sm"
          >
            {t('attributeScoreList.empty')}
          </p>
        )}
        {visibleAttributes.map((attr, index) => (
          <div key={index}>
            {index > 0 && <div className="border-border border-b" />}

            <div
              data-testid={`${testId}-row-${attr.title}`}
              className="flex items-center gap-4 px-3 py-3"
            >
              {/* Attribute label + monthly search volume */}
              <div className="flex flex-1 flex-col gap-0.5">
                <span className="text-foreground text-sm font-normal capitalize">
                  {(attr.title || '').replaceAll(/[-_]/g, ' ')}
                </span>
                {attr.monthlySearches != null && (
                  <span className="text-muted-foreground text-xs font-light">
                    {t('attributeScoreList.searchesPerMonth', {
                      formatted: formatCompactNumber(attr.monthlySearches),
                    })}
                  </span>
                )}
              </div>

              {/* Score bars — legend above provides Before/After context */}
              <div className="w-full max-w-[80%] shrink-0">
                <ScoreComparisonBar
                  beforeScore={attr.beforeScore}
                  afterScore={attr.afterScore}
                  maxScore={attr.maxScore ?? 10}
                  hideLabels={hideComparisonLabel}
                  beforeCount={attr.beforeCount ?? null}
                  afterCount={attr.afterCount ?? null}
                  showBefore={beforeVisible}
                  showAfter={afterVisible}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
